import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle, Button } from '@mui/material';
import TextFileViewer from '@/components/viewers/TextFileViewer';
import SimpleLayout from '@/components/SimpleLayout';

/**
 * Type definition for text file viewer state
 * @typedef {Object} TextFileViewerState
 * @property {string} url - The URL of the text file
 * @property {string} [name] - Optional name of the text file
 */
type TextFileViewerState = {
  url: string;
  name?: string;
};

/**
 * Type definition for error state
 * @typedef {Object} ErrorState
 * @property {string} message - Error message to display
 */
type ErrorState = {
  message: string;
};

/**
 * Text File Viewer Page Component
 * 
 * A full-page component for displaying text files in a user-friendly interface.
 * Retrieves file information from session storage and renders the text content
 * with syntax highlighting and line numbers.
 * 
 * @component
 * @returns {JSX.Element} The text file viewer page component
 * 
 * @example
 * // To use this component, set the file data in session storage:
 * sessionStorage.setItem('textFileViewer', JSON.stringify({
 *   url: 'https://example.com/readme.txt',
 *   name: 'README.txt'
 * }));
 * 
 * @description
 * This component provides:
 * - Full-page text file viewing experience
 * - Syntax highlighting for various file types
 * - Line numbers for better code navigation
 * - Loading states while the file is being loaded
 * - Error handling for missing or invalid file data
 * - Clean navigation with a back button
 */
const TextFileViewerPage: React.FC = () => {
  /**
   * State for managing file data and UI state
   * @type {Object}
   * @property {string} fileUrl - URL of the text file to display
   * @property {string} fileName - Name of the text file (defaults to 'textfile.txt' if not provided)
   * @property {ErrorState | null} error - Error state for handling display errors
   */
  const [fileState, setFileState] = useState<TextFileViewerState>({ url: '', name: 'textfile.txt' });
  const [error, setError] = useState<ErrorState | null>(null);
  
  /**
   * Router navigation hook
   */
  const navigate = useNavigate();

  /**
   * Effect hook to load file data from session storage on component mount
   * @effect
   * @dependencies []
   * @description
   * - Retrieves file data from session storage
   * - Validates the data and updates component state
   * - Sets error state if data is missing or invalid
   */
  useEffect(() => {
    // Get file info from session storage
    const fileInfo = sessionStorage.getItem('textFileViewer');
    
    if (!fileInfo) {
      setError({ message: 'No file information found. Please go back and try again.' });
      return;
    }

    try {
      const parsedData = JSON.parse(fileInfo) as TextFileViewerState;
      if (!parsedData.url) {
        throw new Error('Invalid file URL');
      }
      setFileState({
        url: parsedData.url,
        name: parsedData.name || 'textfile.txt'
      });
    } catch (err) {
      console.error('Error parsing file info:', err);
      setError({ 
        message: 'Invalid file information. Please go back and try again.' 
      });
    }
  }, []);

  /**
   * Handles the close action for the text file viewer
   * @function handleClose
   * @returns {void}
   * @description
   * - Clears the file data from session storage
   * - Navigates back to the previous page
   */
  const handleClose = (): void => {
    // Clear the session storage and navigate back
    sessionStorage.removeItem('textFileViewer');
    navigate(-1); // Go back to previous page
  };

  // Render error state if there's an error
  if (error) {
    return (
      <SimpleLayout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error.message}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        </Box>
      </SimpleLayout>
    );
  }

  // Show loading state while file URL is being loaded
  if (!fileState.url) {
    return (
      <SimpleLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </SimpleLayout>
    );
  }

  // Render the TextFileViewer component with the loaded file data
  return (
    <SimpleLayout>
      <TextFileViewer 
        fileUrl={fileState.url} 
        fileName={fileState.name || 'textfile.txt'} 
        onClose={handleClose} 
      />
    </SimpleLayout>
  );
};

export default TextFileViewerPage;
